import React, { useEffect, useState, useRef } from "react";
// ANT DESIGN COMPONENTS
import { Table, Alert, Empty, Tag } from "antd";
import { TablePaginationConfig } from "antd/es/table";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
	useLazyGetOrganizationsQuery,
	usePostOrganizationMutation,
	usePutOrganizationMutation,
} from "../../../redux/organizations/organizationAPI";
import {
	updateOpenDrawerOrganization,
	updateOrganization,
	updateFormAction,
	updateOrganizationBreadcrumb,
} from "../../../redux/organizations/organizationSlice";
import { useGetCountriesMutation } from "../../../redux/countriesStatesCitiesAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import validatePermission from "../../../utils/validatePermissions";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
import ModalDeleteItemOrganization from "../../components/modal/ModalDeleteItemOrganization";
import OrganizationsTableActions from "./OrganizationsTableActions";
import FormularyRowText from "../../components/formularyRow/FormularyRowText";
import FormularyRowSelectOrganization from "../../components/formularyRow/FormularyRowSelectOrganization";
import FormularyRowSelectCountry from "../../components/formularyRow/FormularyRowSelectCountry";
import FormularyRowSelectState from "../../components/formularyRow/FormularyRowSelectState";
import FormularyRowSelectCity from "../../components/formularyRow/FormularyRowSelectCity";
import FormularyRowPhoneNumber from "../../components/formularyRow/FormularyRowPhoneNumber";
import FormularyRowSelectOrganizationType from "../../components/formularyRow/FormularyRowSelectOrganizationType";
import FormularyHeaderActionsCreate from "../../components/formularyHeader/FormularyHeaderActionsCreate";
import FormularyHeaderActionsEdit from "../../components/formularyHeader/FormularyHeaderActionsEdit";
// INTERFACES
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
import { RootState } from "../../../app/store";
// CONTANTS
import { BLANK_ORGANIZATION } from "../../../constants/organization";
// UTILS
import { getTableRowClass, buildFilterString } from "../../../utils/utils";

const OrganizationTable = () => {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const dropdownRefs = useRef<(HTMLSpanElement | null)[]>([]);
	const { theme, userLogged, globalOrganizationId } = useSelector(
		(state: RootState) => state.home,
	);
	const CUSTOM_ROW: OrganizationInterface = {
		id: "",
		address: "",
		type: "ORG",
		city: "",
		country: "",
		name: "custom",
		phone_code: "+1",
		phone: "",
		state: "",
		parent_id: "",
	};

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [currentOrgId, setCurrentOrgId] = useState<string>("");
	const [organizations, setOrganizations] = useState<OrganizationInterface[]>(
		[],
	);
	const [organization, setOrganization] = useState<OrganizationInterface>({
		...BLANK_ORGANIZATION,
		parent_id: userLogged.org_id,
		country: "United States",
	});
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50", "100"],
		total: 0,
	});

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { permissions } = useSelector((state: RootState) => state.user);
	const {
		organizationRefreshTable,
		tourClickActions,
		formAction,
		organization: selectedOrganization,
		organizationBreadcrumb,
	} = useSelector((state: RootState) => state.organization);
	const [triggerGetCountries, { data: countries }] = useGetCountriesMutation();

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerGetOrganizationsTable,
		{ data, isLoading, isError, isFetching },
	] = useLazyGetOrganizationsQuery();
	const [
		triggerPostOrganization,
		{
			isSuccess: isSuccessPostOrganization,
			isLoading: isLoadingPostOrganization,
			isError: isErrorPostOrganization,
			error: errorPostOrganization,
			reset: resetPostOrganization,
		},
	] = usePostOrganizationMutation();

	const [
		triggerPutOrganization,
		{
			isSuccess: isSuccessPutOrganization,
			isError: isErrorPutOrganization,
			error: errorPutOrganization,
			isLoading: isLoadingPutOrganization,
			reset: resetPutOrganization,
		},
	] = usePutOrganizationMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const getData = async (page: number, limit: number, orgId: string) => {
		const token = await GETJwtToken();
		const searchAndFilterString = buildFilterString({});
		triggerGetOrganizationsTable({
			page,
			limit,
			token,
			parentId: orgId,
			searchAndFilterString,
		}).unwrap();
	};

	const handleTableChange = (newValue: TablePaginationConfig) => {
		setPagination({
			...pagination,
			current: newValue.current,
			pageSize: newValue.pageSize,
			total: newValue.total,
		});
		getData(newValue.current || 1, newValue.pageSize || 10, currentOrgId);
	};

	const checkFormIsValid = () => {
		if (
			organization.name === "" ||
			organization.parent_id === "" ||
			organization.address === "" ||
			organization.country === "" ||
			organization.state === "" ||
			organization.city === "" ||
			organization.phone === ""
		) {
			return true;
		}
		return false;
	};

	const postOrganization = async () => {
		const token = await GETJwtToken();
		const BODY = {
			body: organization,
			token,
		};
		triggerPostOrganization(BODY);
	};

	const putOrganization = async () => {
		const token = await GETJwtToken();
		const BODY = {
			id: organization.id,
			body: organization,
			token,
		};
		triggerPutOrganization(BODY);
	};

	const onClickSave = async () => {
		if (formAction === "create") {
			postOrganization();
		} else if (formAction === "edit") {
			putOrganization();
		}
	};

	const onClickResetForm = () => {
		dispatch(updateFormAction("create"));
		resetPostOrganization();
		resetPutOrganization();
		setOrganization({
			...BLANK_ORGANIZATION,
			parent_id: globalOrganizationId,
			country: "United States",
		});
		setCurrentOrgId(globalOrganizationId);
		setPagination({
			...pagination,
			current: 1,
			pageSize: 10,
		});
		getData(1, 10, globalOrganizationId);
		dispatch(updateOrganizationBreadcrumb([]));
	};

	const onChangeFormValue = (
		newValue: string | number,
		variableName: string,
	) => {
		if (variableName !== "") {
			const copy: OrganizationInterface = structuredClone(organization);
			if (variableName === "name" && typeof newValue === "string") {
				copy.name = newValue;
			} else if (variableName === "address" && typeof newValue === "string") {
				copy.address = newValue;
			} else if (
				variableName === "state" &&
				typeof newValue === "string" &&
				newValue !== ""
			) {
				copy.state = newValue;
			} else if (
				variableName === "city" &&
				typeof newValue === "string" &&
				newValue !== ""
			) {
				copy.city = newValue;
			} else if (
				variableName === "country" &&
				typeof newValue === "string" &&
				newValue !== ""
			) {
				copy.country = newValue;
			} else if (variableName === "phone" && typeof newValue === "string") {
				copy.phone = newValue;
			} else if (variableName === "parent_id" && typeof newValue === "string") {
				if (newValue !== process.env.REACT_APP_MASTER_ORG_ID) {
					copy.type = "SUBORG";
				} else {
					copy.type = "ORG";
				}
				copy.parent_id = newValue;
			} else if (
				variableName === "type" &&
				typeof newValue === "string" &&
				(newValue === "ORG" || newValue === "SUBORG")
			) {
				copy.type = newValue;
			}
			setOrganization(copy);
		}
	};

	const onClickNavigateToSubOrganizations = (record: OrganizationInterface) => {
		if (formAction === "create" && record.id !== "") {
			dispatch(
				updateOrganizationBreadcrumb([
					...organizationBreadcrumb,
					{ orgId: record.id, name: record.name },
				]),
			);
		}
	};

	const onClickRowText = (record: OrganizationInterface) => {
		if (record && formAction === "create" && record.id !== "") {
			dispatch(updateOrganization(record));
			dispatch(updateOpenDrawerOrganization(true));
		}
	};

	// ************************************************* */
	// TABLE COLUMNS *********************************** */
	const columns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			onCell: (record: any) => ({
				onClick: () => {
					onClickNavigateToSubOrganizations(record);
				},
			}),
			render: (text: string, record: OrganizationInterface) => (
				<FormularyRowText
					record={record}
					text={text}
					value={organization.name}
					inputIcon
					placeHolder={
						organization.type === "ORG"
							? t("general.newOrganization")
							: t("general.newSubOrganization")
					}
					formAction={formAction}
					variableName='name'
					itemId={organization.id}
					inputValidationType='na'
					textLink
					onChangeFormValue={onChangeFormValue}
				/>
			),
		},
		{
			title: "Organization",
			dataIndex: "parent_id",
			key: "parent_id",
			onCell: (record: any) => ({
				onClick: () => {
					onClickRowText(record);
				},
			}),
			render: (text: string, record: OrganizationInterface) => (
				<FormularyRowSelectOrganization
					record={record}
					text={text}
					value={organization.parent_id}
					inputIcon={false}
					formAction={formAction}
					variableName='parent_id'
					itemId={organization.id}
					textLink={false}
					onChangeFormValue={onChangeFormValue}
				/>
			),
		},
		{
			title: "Address",
			dataIndex: "address",
			key: "address",
			onCell: (record: any) => ({
				onClick: () => {
					onClickRowText(record);
				},
			}),
			render: (text: string, record: OrganizationInterface) => (
				<FormularyRowText
					record={record}
					text={text}
					value={organization.address}
					inputIcon={false}
					placeHolder={t("general.address")}
					formAction={formAction}
					variableName='address'
					itemId={organization.id}
					inputValidationType='na'
					textLink={false}
					onChangeFormValue={onChangeFormValue}
				/>
			),
		},
		{
			title: "Country",
			dataIndex: "country",
			key: "country",
			onCell: (record: any) => ({
				onClick: () => {
					onClickRowText(record);
				},
			}),
			render: (text: string, record: OrganizationInterface) => (
				<FormularyRowSelectCountry
					record={record}
					text={text}
					value={organization.country}
					inputIcon={false}
					formAction={formAction}
					itemId={organization.id}
					textLink={false}
					onChangeFormValue={onChangeFormValue}
					countries={countries}
				/>
			),
		},
		{
			title: "State",
			dataIndex: "state",
			key: "state",
			onCell: (record: any) => ({
				onClick: () => {
					onClickRowText(record);
				},
			}),
			render: (text: string, record: OrganizationInterface) => (
				<FormularyRowSelectState
					record={record}
					text={text}
					value={organization.state}
					inputIcon={false}
					formAction={formAction}
					itemId={organization.id}
					textLink={false}
					onChangeFormValue={onChangeFormValue}
					country={organization.country}
				/>
			),
		},
		{
			title: "City",
			dataIndex: "city",
			key: "city",
			onCell: (record: any) => ({
				onClick: () => {
					onClickRowText(record);
				},
			}),
			render: (text: string, record: OrganizationInterface) => (
				<FormularyRowSelectCity
					record={record}
					text={text}
					value={organization.city}
					inputIcon={false}
					formAction={formAction}
					itemId={organization.id}
					textLink={false}
					onChangeFormValue={onChangeFormValue}
					country={organization.country}
					countryState={organization.state}
				/>
			),
		},
		{
			title: "Phone Number",
			dataIndex: "phone",
			key: "phone",
			onCell: (record: any) => ({
				onClick: () => {
					onClickRowText(record);
				},
			}),
			render: (text: string, record: OrganizationInterface) => (
				<FormularyRowPhoneNumber
					record={record}
					text={text}
					value={organization.phone}
					formAction={formAction}
					itemId={organization.id}
					textLink={false}
					onChangeFormValue={onChangeFormValue}
				/>
			),
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			onCell: (record: any) => ({
				onClick: () => {
					onClickRowText(record);
				},
			}),
			render: (text: string, record: OrganizationInterface) => (
				<FormularyRowSelectOrganizationType
					record={record}
					text={text}
					value={organization.type}
					formAction={formAction}
					itemId={organization.id}
					textLink={false}
					onChangeFormValue={onChangeFormValue}
				/>
			),
		},
		{
			title: "Actions",
			dataIndex: "actioins",
			key: "actions",
			render: (_: any, record: OrganizationInterface) => (
				<>
					{record.id === "" ? (
						<FormularyHeaderActionsCreate
							formAction={formAction}
							formIsValid={checkFormIsValid()}
							onClickSave={onClickSave}
							onClickResetForm={onClickResetForm}
						/>
					) : (
						<>
							{formAction === "edit" && record.id === organization.id ? (
								<FormularyHeaderActionsEdit
									formIsValid={checkFormIsValid()}
									onClickSave={onClickSave}
									onClickResetForm={onClickResetForm}
								/>
							) : (
								<OrganizationsTableActions selectedItem={record} />
							)}
						</>
					)}
				</>
			),
		},
	];

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		triggerGetCountries({});
	}, []);

	useEffect(() => {
		if (data && data.data) {
			setPagination({
				...pagination,
				total: data.total,
			});
			setOrganizations(data.data);
		}
	}, [data]);

	useEffect(() => {
		if (tourClickActions) {
			if (dropdownRefs.current[0]) {
				dispatch(updateOrganization(organizations[0]));
				dropdownRefs.current[0].click();
			}
		}
	}, [tourClickActions]);

	useEffect(() => {
		if (organizationRefreshTable) {
			onClickResetForm();
		}
	}, [organizationRefreshTable]);

	useEffect(() => {
		if (
			isSuccessPostOrganization ||
			isSuccessPutOrganization ||
			isErrorPostOrganization ||
			isErrorPutOrganization
		) {
			onClickResetForm();
		}
	}, [
		isSuccessPostOrganization,
		isSuccessPutOrganization,
		isErrorPostOrganization,
		isErrorPutOrganization,
	]);

	useEffect(() => {
		if (
			selectedOrganization &&
			selectedOrganization.id &&
			selectedOrganization.id !== ""
		) {
			setOrganization({ ...selectedOrganization });
		}
	}, [selectedOrganization]);

	useEffect(() => {
		if (organizationBreadcrumb.length > 0 && globalOrganizationId !== "") {
			getData(
				1,
				10,
				organizationBreadcrumb[organizationBreadcrumb.length - 1].orgId,
			);
			setCurrentOrgId(
				organizationBreadcrumb[organizationBreadcrumb.length - 1].orgId,
			);
			onChangeFormValue(
				organizationBreadcrumb[organizationBreadcrumb.length - 1].orgId,
				"parent_id",
			);
		}
	}, [organizationBreadcrumb]);

	useEffect(() => {
		if (globalOrganizationId !== "") {
			getData(1, 10, globalOrganizationId);
			setCurrentOrgId(globalOrganizationId);
			onChangeFormValue(globalOrganizationId, "parent_id");
			dispatch(updateOrganizationBreadcrumb([]));
		}
	}, [globalOrganizationId]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='mt-3'>
			{validatePermission("orgs-view", permissions) ? (
				<>
					{!isError ? (
						<Table
							rowClassName={(record, index) => {
								if (
									(index === 0 && formAction === "create") ||
									(formAction === "edit" && record.id === organization.id)
								) {
									return "table-row-active";
								}
								return getTableRowClass(index, theme);
							}}
							className='my-custom-table'
							dataSource={[CUSTOM_ROW, ...organizations]}
							loading={
								isLoading ||
								isFetching ||
								isLoadingPostOrganization ||
								isLoadingPutOrganization
							}
							rowKey={(record) => record.id}
							size='small'
							pagination={pagination}
							onChange={handleTableChange}
							footer={() => (
								<div className='generalStyles__flexEnd'>
									<Tag>{pagination.total} Rows</Tag>
								</div>
							)}
							scroll={{ x: 900 }}
							columns={columns}
						/>
					) : (
						<Alert
							message={t("organizationManagement.errorDescription.error")}
							description={t("organizationManagement.errorDescription.text1")}
							type='error'
							showIcon
							className='mt-3'
						/>
					)}
				</>
			) : (
				<>
					<div className='generalStyles__noAccessToListTable'>
						<Empty
							description={t(
								"organizationManagement.listNotAvailableOrganizations",
							)}
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					</div>
				</>
			)}
			{/** DELETE ITEM COMPONENT */}
			<ModalDeleteItemOrganization
				nameComponent='organization'
				nameItem={organization.name}
				id={organization.id}
			/>
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPostOrganization}
				isSuccess={isSuccessPostOrganization}
				requestType='POST'
				error={errorPostOrganization}
				name='Organization'
			/>
			<GlobalAlert2
				isError={isErrorPutOrganization}
				isSuccess={isSuccessPutOrganization}
				requestType='PUT'
				error={errorPutOrganization}
				name='Organization'
			/>
		</div>
	);
};

export default OrganizationTable;
